import React from 'react';
import { Avatar } from '@cian/ui-kit';
import { HonestWork, PopupDirection } from '@cian/honestwork-component';

import { IconCianLogo12 } from '../Icons/IconCianLogo12';
import s from './UserCard.css';

export interface IUserCardParams {
  /** Ссылка на изображение */
  imageSrc?: string;
  /** Имя пользователя */
  name: string;
  /** Описание */
  description?: string;
  /** Размер аватарки */
  avatarSize?: 'XL' | 'L' | 'M' | 'S' | number;
  /** Размер шрифта имени */
  fontSize?: 'M' | 'S';
  /** Иконка доверенного пользователя */
  isTrusted?: boolean;
  /** Ссылка пользователя */
  url?: string;
  /** Событие клика */
  onClick?(e: React.MouseEvent): void;
  /** Выделить ли имя жирным стилем */
  isStrongName?: boolean;
  /** Показывать ли аватар */
  hasAvatar?: boolean;
  /** Дополнительный контент после имени */
  afterName?: React.ReactNode;
  /** Дополнительный контент под всем текстом */
  afterAll?: React.ReactNode;
  /** Показывать ли логотип Циана на аватарке */
  withCianLogo?: boolean;
  /** Скрывать ли текстовый контент в ellipsis */
  hasEllipsisContent?: boolean;
}

/**
 * Карточка пользователя
 */
export const UserCard = ({
  imageSrc,
  name,
  description,
  avatarSize = 'M',
  fontSize = 'M',
  isTrusted = false,
  url,
  onClick,
  isStrongName,
  hasAvatar = true,
  afterName,
  afterAll,
  withCianLogo,
  hasEllipsisContent = true,
}: IUserCardParams) => {
  const avatarSizes = {
    XL: 64,
    L: 48,
    M: 40,
    S: 30,
  };

  const fontSizeClasses = {
    M: '_fs16',
    S: '_fs14',
  };

  const avatarSizeAsNumber = typeof avatarSize === 'number' ? avatarSize : avatarSizes[avatarSize];

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();

    e.stopPropagation();
    if (onClick) {
      onClick(e);
    }
  };

  return (
    <div className={s['wrapper']}>
      {hasAvatar && (
        <div className={s['image']} onClick={handleClick}>
          {url ? (
            <a href={url} className={s['image-link']} onClick={handleClick}>
              {withCianLogo && (
                <div className={s['cian-logo']}>
                  <IconCianLogo12 />
                </div>
              )}

              <Avatar src={imageSrc} type="user" size={avatarSizeAsNumber} />
            </a>
          ) : (
            <Avatar src={imageSrc} type="user" size={avatarSizeAsNumber} />
          )}
        </div>
      )}

      <div className={`${s['content']} ${hasEllipsisContent ? s['_ellipsis'] : ''}`}>
        <div className={s['name-wrapper']}>
          {url ? (
            <a
              href={url}
              className={`${s['name']} ${s[fontSizeClasses[fontSize]]} ${isStrongName ? s['_strong'] : ''}`}
              onClick={handleClick}
              title={name}
            >
              {name}
            </a>
          ) : (
            <div
              className={`${s['name']} ${s[fontSizeClasses[fontSize]]} ${isStrongName ? s['_strong'] : ''}`}
              onClick={handleClick}
              title={name}
            >
              {name}
            </div>
          )}

          {isTrusted && <HonestWork className={s['icon-trusted']} direction={PopupDirection.RIGHT} />}
          {afterName}
        </div>

        {description && (
          <div className={s['description']} title={description}>
            {description}
          </div>
        )}

        {afterAll}
      </div>
    </div>
  );
};
